// Copyright 2019 Drivetrain Hub LLC
// JavaScript utility functions.

import math from "./math";
import { formatCoordStr } from "./coord";

// region COPIES
// https://stackoverflow.com/questions/728360/how-do-i-correctly-clone-a-javascript-object?noredirect=1&lq=1

export function copyShallow(obj) {
  // Create copy one level deep, with nested objects referencing the original obj.

  return Object.assign({}, obj);
}

export function copyDeepJSON(obj) {
  // Create deep copy except for Date type, unless first converted to string.
  // This also works to deep copy an array of objects.
  // https://medium.com/@gamshan001/javascript-deep-copy-for-array-and-object-97e3d4bc401a

  return JSON.parse(JSON.stringify(obj));
}

export function copyDeep(obj) {
  let copy;

  // Handle the 3 simple types, and null or undefined
  if (null == obj || "object" != typeof obj) return obj;

  // Handle Date
  if (obj instanceof Date) {
    copy = new Date();
    copy.setTime(obj.getTime());
    return copy;
  }

  // Handle Array
  if (obj instanceof Array) {
    copy = [];
    for (var i = 0, len = obj.length; i < len; i++) {
      copy[i] = copyDeep(obj[i]);
    }
    return copy;
  }

  // Handle Object
  if (obj instanceof Object) {
    copy = {};
    for (let attr in obj) {
      if (obj.hasOwnProperty(attr)) copy[attr] = copyDeep(obj[attr]);
    }
    return copy;
  }

  throw new Error("Unable to copy obj! Its type isn't supported.");
}

// endregion

// region OBJECTS

export function pop(obj, key) {
  const val = obj[key];
  delete obj[key];
  return val;
}

export function isObj(val) {
  // null evaluates as an object, hence the null check
  return typeof val === 'object' && val !== null;
}

/**
 * Check if an object is empty.  Values of `null` and `undefined` also return `true`, implying empty objects.
 * https://coderwall.com/p/_g3x9q/how-to-check-if-javascript-object-is-empty
 * @param {Object} obj - Object to check if empty.
 * @return {boolean} `true` if object is empty, otherwise `false`.
 */
export function isEmptyObj(obj) {
  for (let key in obj) {
    if (obj.hasOwnProperty(key))
      return false;
  }
  return true;
}

export function hasDefinedValues(obj, skipProps=[]) {
  // Check if all all object values are defined, specifically that values are not null or an empty string.
  // Originally created to check if form fields are defined before performing an action.
  // Returns boolean to indicate if all values are defined.

  for (const [key, val] of Object.entries(obj)) {
    if (skipProps.includes(key)) continue;
    if (val === null || val === "") return false;
    // if (isNaN(Number(val))) return false;
  }
}

export function isSameObjValues(objInit, objDyn) {
  // Recursively compare key-value pairs between two objects, including comparison of values that are arrays.
  // objDyn must have all the same keys as objInit, but may have more.

  for (let prop in objInit) {
    if (objInit.hasOwnProperty(prop)) {
      let valueInit = objInit[prop];
      let valueDyn = objDyn[prop];

      // DEV ONLY
      // console.log('prop', prop);
      // console.log('valueInit', valueInit);
      // console.log('valueDyn', valueDyn);

      if (valueInit === valueDyn) {
        continue
      }
      // if (valueInit === valueDyn) {return}
      if (Array.isArray(valueInit)) {
        // compare array values
        if (valueInit.length !== valueDyn.length) {
          return false;
        } else {
          for (let i = 0; i < valueInit.length; i++) {
            if (valueInit[i] !== valueDyn[i]) {
              return false;
            }
          }
        }
      } else if (isObj(valueInit)) {
        // recursive (NOTE: typeof [] is an object, hence isArray is used above)
        return isSameObjValues(valueInit, valueDyn)

      } else if (valueInit !== valueDyn) {
        return false;
      }
    }
  }
  return true;
}

export function nullifyObj(obj) {
  // Recursively set all prop values with an empty string '' to null.
  // GraphQL does not consider an empty string as an unspecified field.
  // Operates directly on object, but also returns the nullified object.

  Object.keys(obj).forEach(key => {
    if (isObj(obj[key])) {
      nullifyObj(obj[key])
    } else if (obj[key] === '') {
      obj[key] = null;
    }
  });

  return obj;
}

function lookupObj(objArray, propertyIndex, propertyValue) {
  // Lookup object based on the value of one of its properties.  Intended for use with unique IDs.
  // Ex)
  //  arr: [{uid: '123', a: '1', b: '2'}, {uid: '456', a: '3', b: '4'}]
  //  uidIndex = arr.map(item => item.uid);
  //  lookupObj(arr, uidIndex, '456')
  //  returns {uid: '456', a: '3', b: '4'}

  return objArray[propertyIndex.indexOf(propertyValue)];
}

export function combineObj() {
  // Combine multiple objects with shallow or deep merging.
  // For shallow merging, it is recommended to use Object.assign() instead.
  // Example of deep merging two objects:  combineObj(true, obj1, obj2)
  // https://gomakethings.com/merging-objects-with-vanilla-javascript/
  // https://vanillajstoolkit.com/helpers/extend/

  // Variables
  let extended = {};
  let deep = false;
  let i = 0;

  // Check if a deep merge
  if ( Object.prototype.toString.call( arguments[0] ) === '[object Boolean]' ) {
    deep = arguments[0];
    i++;
  }

  // Merge the object into the extended object
  let merge = function (obj) {
    for (let prop in obj) {
      if (obj.hasOwnProperty(prop)) {
        // If property is an object, merge properties
        if (deep && Object.prototype.toString.call(obj[prop]) === '[object Object]') {
          extended[prop] = combineObj(extended[prop], obj[prop]);
        } else {
          extended[prop] = obj[prop];
        }
      }
    }
  };

  // Loop through each object and conduct a merge
  for (; i < arguments.length; i++) {
    merge(arguments[i]);
  }

  return extended;
}

export function assignProps(objTo, objFrom) {
  // Recursively assign props from one object to another, assigning only props that already exist in objTo.
  // Props are not assigned if the from-value is an empty string.
  // Operates directly on object, but also returns the object.

  let valFrom;
  Object.keys(objTo).forEach(key => {
    if (objFrom.hasOwnProperty(key)) {
      valFrom = objFrom[key];

      if (isObj(objTo[key]) && valFrom !== null) {
        // recursively calls if to-value is object and from-value is not null
        assignProps(objTo[key], valFrom)
      } else if (valFrom !== '') {
        // assigns if from-value is not empty string
        objTo[key] = valFrom;
      }
    }
  });

  return objTo
}

/**
 * Get an array of an object's values.
 * References:
 *  1. https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_objects/Object/values
 *  2. https://stackoverflow.com/questions/38824349/how-to-convert-an-object-to-an-array-of-key-value-pairs-in-javascript
 * @param {Object} obj - Object to get array of its values.
 * @return {Array} Array of object values.
 */
export function objToArr(obj) {
  return Object.values(obj);
}

/**
 * Map array to object values.  This expects array length to equal the number of object properties.
 * @param obj - Object to operate on, assigning the arr to its property values.
 * @param arr - Array to assign as values of object properties.
 */
export function objMap(obj, arr) {
  // Map array to object values.  This expects array length to equal the number of object properties.
  // References:
  //  1. https://stackoverflow.com/questions/14810506/map-function-for-objects-instead-of-arrays

  Object.keys(obj).map(function(key, index) {
    obj[key] = arr[index];
  });
}

export function objRoundNumbers(obj, digits) {
  // Mutates object props of number type by rounding them.
  // Useful when unit conversions cause poorly displayed numbers.

  for (const [key, value] of Object.entries(obj)) {
    if (typeof value === 'number') {
      obj[key] = math.round(value, digits);
    }
  }

  return obj;
}

export function objNumbers2Strings(obj) {
  // Recursively mutate object props of number type by making them strings.
  // Useful to avoid form validation problems with the number zero.

  for (const [key, value] of Object.entries(obj)) {
    switch (typeof value) {
      case "number":
        obj[key] = value.toString();
        break;
      case "object":
        if (value !== null) {
          obj[key] = objNumbers2Strings(obj[key]);
        }
    }
  }

  return obj;
}

// endregion

// region ARRAYS

export function isEmptyArr(arr) {
  // Check if array is empty, and return boolean.
  // Unsure if this will work for other data types.

  return (!arr || 0 === arr.length);
}

export function addArrays(arr1, arr2) {
  // Element-wise addition of two arrays.
  // https://stackoverflow.com/questions/7135874/element-wise-operations-in-javascript

  let result = [];
  for (let i = 0; i < arr1.length; i++) result.push(arr1[i] + arr2[i]);

  return result;
}

export function subtractArrays(arr1, arr2) {
  // Element-wise subtraction of two arrays.
  // https://stackoverflow.com/questions/7135874/element-wise-operations-in-javascript

  let result = [];
  for (let i = 0; i < arr1.length; i++) result.push(arr1[i] - arr2[i]);

  return result;
}

export function removeItem(arr, item) {
  // Remove a specific element from an array.
  // https://stackoverflow.com/questions/5767325/how-do-i-remove-a-particular-element-from-an-array-in-javascript

  let index = arr.indexOf(item);
  if (index > -1) {
    arr.splice(index, 1);
  }
}

export function insertItem(arr, item, index) {
  // Insert an element into an array at a specific index.
  // Operates on array object, returns nothing.
  // https://stackoverflow.com/questions/586182/how-to-insert-an-item-into-an-array-at-a-specific-index-javascript

  arr.splice(index, 0, item);
}

export function arraysEqual(a, b, matchOrder=true) {
  // https://gomakethings.com/how-to-check-if-two-arrays-are-equal-with-vanilla-js/

  if (a === b) return true;
  if (a == null || b == null) return false;
  if (a.length !== b.length) return false;

  // If the order of array elements does not matter, sort both arrays.
  // WARNING: sort operates on the array itself, so consider cloning the array first.
  let ac, bc;
  if (!matchOrder) {
    ac = a.slice(0);
    bc = b.slice(0);
    ac.sort();
    bc.sort();
  } else {
    ac = a;
    bc = b;
  }

  for (let i = 0; i < ac.length; ++i) {
    if (ac[i] !== bc[i]) return false;
  }
  return true;
}

/**
 * Sort an array of objects by its property, where the property is a String.
 *  1. https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Array/sort
 * @param {Array} arr - Array object to sort.
 * @param {string} prop - String of property to sort by.  Property value must be a String.
 */
export function sortByString(arr, prop) {

  return arr.sort(function(a, b) {
    const nameA = a[prop].toUpperCase(); // ignore upper and lowercase
    const nameB = b[prop].toUpperCase(); // ignore upper and lowercase

    if (nameA < nameB) {
      return -1;
    }
    if (nameA > nameB) {
      return 1;
    }

    // names must be equal
    return 0;
  });
}

/**
 * Get an object of an array, with object values matching the array values.  Object properties are integers, starting 0.
 * @param {Array} arr - Array to use for object values.
 * @return {*} - Object with values matching array, and object properties are integers starting at 0.
 */
export function arrToObj(arr) {
  // References:
  //  1. https://stackoverflow.com/questions/4215737/convert-array-to-object
  //  2. https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Operators/Spread_syntax

  // APPROACH 1
  return Object.assign({}, arr);

  // APPROACH 2 - less supported as of Dec 2018
  // return { ...arr }
}

export function zip(a, b) {
  // https://stackoverflow.com/questions/22015684/how-do-i-zip-two-arrays-in-javascript

  return a.map(function(el, i) {
    return [el, b[i]];
  });
}

// endregion

// region STRINGS

/**
 * Capital the first character of a string.
 * References:
 *  1. https://stackoverflow.com/questions/1026069/how-do-i-make-the-first-letter-of-a-string-uppercase-in-javascript
 * @param {string} str - String to capitalize its first character.
 * @return {string} String with first character capitalized.
 */
export function capitalizeFirstLetter(str) {
  return str.charAt(0).toUpperCase() + str.slice(1);
}

/**
 * Limit character count to avoid excessively wide tabs, appending '..' in place of last three characters.
 * @param {string} str - String to limit its characters.
 * @param {number} limit - Character limit.
 * @return {string} Truncated string, ending in '..', if character limit is exceeded.
 */
export function limitChars(str, limit) {

    if (str.length > limit) {
      str = str.slice(0, limit - 2);
      str = str.concat('..');
    }
    return str;
}

/**
 * Camelize a string, cutting the string by multiple separators like hyphens, underscores and spaces.
 *
 * References:
 * 1. https://ourcodeworld.com/articles/read/608/how-to-camelize-and-decamelize-strings-in-javascript
 *
 * @param text String to camelize
 * @return string Camelized text
 */
export function camelize(text) {
  return text.replace(/^([A-Z])|[\s-_]+(\w)/g, function(match, p1, p2) {
    if (p2) return p2.toUpperCase();
    return p1.toLowerCase();
  });
}

/**
 * Decamelizes a string with/without a custom separator (underscore by default).
 *
 * References:
 * 1. https://ourcodeworld.com/articles/read/608/how-to-camelize-and-decamelize-strings-in-javascript
 *
 * @param str String in camelcase
 * @param separator Separator for the new decamelized string.
 */
export function decamelize(str, separator){
  separator = typeof separator === 'undefined' ? '_' : separator;

  return str
    .replace(/([a-z\d])([A-Z])/g, '$1' + separator + '$2')
    .replace(/([A-Z]+)([A-Z][a-z\d]+)/g, '$1' + separator + '$2')
    .toLowerCase();
}

/**
 * Recursively parse all object values with Number.parseFloat.
 * @param {{}} obj - Object to parse.
 * @return {{}} New object with parsed values.
 */
export function makeFloats(obj) {
  // This is particularly useful when converting form fields to an object required by calculations.

  let objFloats = {};
  let val;

  Object.keys(obj).forEach(key => {
    val = obj[key];
    if (isObj(val)) {
      makeFloats(val)
    } else {
      objFloats[key] = Number.parseFloat(val);
    }
  });

  return objFloats;
}

/**
 * Nullify an empty string. GraphQL does not consider empty string as unspecified.
 * @param {string} str - String to nullify.
 * @return {*} Null if empty string, otherwise returns original string.
 */
export function nullifyStr(str) {
  return str === '' ? null : str;
}

function parseCssCalc(calcStr) {
  // Parse CSS calc string, e.g. 'calc(-1em + 491px)', and return array: ['-1em', '491px']
  // To get a calculated style property value, use:  window.getComputedStyle(el).<styleProperty>
  // To parse calculated property:  parseInt(window.getComputedStyle(el).<styleProperty>.slice(0, -2))

  let eqn = calcStr.slice(5, -1);  // e.g. '-1em + 491px'
  let iOp = eqn.indexOf(' + ');
  if (!iOp) iOp = eqn.indexOf(' - ');
  let a = eqn.slice(0, iOp);
  let b = eqn.slice(iOp + 3);

  return [a, b];
}

// endregion

// region BOOLEANS

/**
 * Return a string based on boolean value.
 * @param {Boolean} bool - Boolean value to convert to a string.
 * @param {string} trueStr - String if true.
 * @param {string} falseStr - String if false.
 * @return {String} String based on boolean value.
 */
export function boolStr(bool, trueStr, falseStr) {
  return bool ? trueStr : falseStr;
}

// endregion

// region DATETIME

export function datetimeNow() {
  // Returns string of current datetime in the same ISO format used by database.
  return new Date().toISOString().replace('Z', '000');
}

// endregion

// region FILES

/**
 * Convert a File or Blob object to a base64 encoded string of ASCII characters.
 * @param {File | Blob} blob - File or blob object to convert.
 * @return {Promise<unknown>} Promise that resolves when file has been converted.
 */
export function createBase64String(blob) {
  // https://stackoverflow.com/questions/36280818/how-to-convert-file-to-base64-in-javascript
  // https://developer.mozilla.org/en-US/docs/Web/API/FileReader
  // https://developer.mozilla.org/en-US/docs/Web/API/FileReader/readAsDataURL

  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(blob);
    reader.onload = () => resolve(reader.result.replace(/^.*,/, ''));  // replaces metadata
    reader.onerror = error => reject(error);
  })
}

export function createDataUrl(data, dataType='application/octet-stream') {
  const blob = new Blob([data], {'type':dataType});
  return window.URL.createObjectURL(blob);
}

/**
 * Initiate a file download.
 *
 * References:
 *  #. https://stackoverflow.com/a/19230668/5995422
 *  #. https://stackoverflow.com/questions/19721439/download-json-object-as-a-file-from-browser
 *  #. https://developer.mozilla.org/en-US/docs/Web/API/Blob
 *  #. https://stuk.github.io/jszip/documentation/examples/download-zip-file.html
 *  #. Alternative: https://www.npmjs.com/package/file-saver/v/1.3.2
 *
 * @param data - Data object, such as JavaScript Object, base64 object as generated by JSZip, or other.
 * @param {string} filename - String of download filename, including file extension.
 * @param {string} dataType - String indicating the MIME type of the data.
 */
export function downloadFile(data, filename, dataType=undefined) {
  // Example MIME types:
  //  text/json
  //  application/zip

  const dataURL = createDataUrl(data, dataType);

  // create and click download link
  let a = document.createElement('a');
  a.href = dataURL;
  a.download = filename;
  a.click();

  // cleanup
  a.remove();
  URL.revokeObjectURL(dataURL);
}

// endregion

// region MISC

/**
 * Format a value for display.  For numbers, round then to the third digit.  For booleans, convert to 'Yes' or 'No'.
 * @param {*} val - Value to format.
 * @return {String} Formatted string for display.
 */
export function formatValue(val) {
  switch (typeof val) {
    case 'boolean':
      return boolStr(val, 'Yes', 'No');
    case 'number':
      return math.round(val, 3);
    case 'array':
        return formatCoordStr(val);
    case 'object':  // null
    case 'undefined':
      return '-';
    default:
      return val;
  }
}

/**
 * Check if a variable exists, where variable could be string, number, object, function, etc.
 * Avoids evaluating to false if variable equals false or the number 0.
 * References:
 *  1. https://stackoverflow.com/questions/5113374/javascript-check-if-variable-exists-is-defined-initialized
 *
 * @param {*} variable - Variable to check for existence.
 * @return {boolean} Boolean to indicate if variable exists.
 */
export function isDefined(variable) {
  return typeof variable !== 'undefined';
}

/**
 * Print a specific HTML element.
 * References:
 *  1. https://stackoverflow.com/questions/2255291/print-the-contents-of-a-div
 *  @param {HTMLElement || Object} elem - HTML element to print.
 */
export function printElement(elem) {

  // create a temporary window for assigning element HTML only
  let newWindow = window.open('', 'PRINT', 'height=400,width=600');
  newWindow.document.write('<html lang="en"><head><title>' + document.title  + '</title>');
  newWindow.document.write('</head><body >');

  // transfer style of element to print document
  // WARNING: Not all styles hold up.  There may a better way to do this.
  const style = window.getComputedStyle(elem);
  for (let prop of style) {
    newWindow.document.body.style[prop] = style[prop];
  }

  newWindow.document.write(elem.innerHTML);
  newWindow.document.write('</body></html>');

  newWindow.document.close(); // necessary for IE >= 10
  newWindow.focus(); // necessary for IE >= 10*/

  newWindow.print();
  newWindow.close();

  return true;
}

export async function timeout(ms) {
  // Useful to use in while-loop to wait for user input.
  return new Promise(res => setTimeout(res, ms));
}

/**
 * Polling function to call an async function at a specified interval.
 * @param {function} fn - Async function to call at the specified interval.
 * @param {function} validate - Function to validate response from async function.
 * @param {number} interval - Number of milliseconds between calls.
 * @param {number} maxAttempts - Maximum number of async function calls.
 * @return {Promise<unknown>} Promise of polling function.
 */
export async function poll({ fn, validate, interval, maxAttempts }) {
  // https://levelup.gitconnected.com/polling-in-javascript-ab2d6378705a

  let attempts = 0;

  const executePoll = async (resolve, reject) => {
    const result = await fn();
    attempts++;

    if (validate(result)) {
      return resolve(result);
    } else if (maxAttempts && attempts === maxAttempts) {
      return reject(new Error('Exceeded max attempts'));
    } else {
      setTimeout(executePoll, interval, resolve, reject);
    }
  };

  return new Promise(executePoll);
}

export class ResizeListener {
  // Influenced by: https://stackoverflow.com/questions/6492683/how-to-detect-divs-dimension-changed
  // References:
  //  1. https://developer.mozilla.org/en-US/docs/Web/API/MutationObserver
  //  2. https://developer.mozilla.org/en-US/docs/Web/API/MutationObserverInit

  constructor(element, callback, delay=0, windowResize=true) {
    // delay can be useful to wait for transitions before running callback, e.g. to limit jitter

    this.callback = callback;
    this.delay = delay;

    // Save the element we are watching
    this.element = element;
    this.offsetWidth = element.offsetWidth;
    this.offsetHeight = element.offsetHeight;

    // Listen to the element being checked for width and height changes
    this.observer = new MutationObserver(this.checkForChanges.bind(this));
    this.observer.observe(element, {
      attributes: true,
      subtree: true,
    });

    // Listen to the window resize event
    if (windowResize) window.addEventListener('resize', callback);
  }
  checkForChanges() {
    // args: mutationsList, observer
    // setTimeout required to allow time for element changes to occur and be detected.

    window.setTimeout(() => {
      // DEV ONLY
      // console.log('width changed', this.element.width !== this.width);
      // console.log('offsetWidth changed', this.element.offsetWidth !== this.offsetWidth);
      // console.log('offsetHeight changed', this.element.offsetHeight !== this.offsetHeight);

      if (this.element.offsetWidth !== this.offsetWidth || this.element.offsetHeight !== this.offsetHeight) {
        this.offsetWidth = this.element.offsetWidth;
        this.offsetHeight = this.element.offsetHeight;
        this.callback();
      }
    }, this.delay);
  }
  destroy() {
    window.removeEventListener('resize', this.callback);
    this.observer.disconnect();
  }
}

// endregion

export default {
  // COPIES
  copyShallow,
  copyDeepJSON,
  copyDeep,

  // OBJECTS
  isEmptyObj,
  isSameObjValues,
  lookupObj,

  // ARRAYS
  isEmptyArr,
  addArrays,
  removeItem,
  arraysEqual,

  // STRINGS
  capitalizeFirstLetter,
  camelize,
  decamelize,
  parseCssCalc,
}
